import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { cleanName, orNull } from '../../../common/utils/text';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useDeleteRace, useRace, useUpdateRace } from '../contexts/races';
import { validateRace } from '../utils/validate';
import RaceFields from './fields';

import '../../../common/styles/catalog.css';

function buildForm(race) {
    return { name: race.name || '', code: race.code || '', status: race.status ?? 2 };
}

export default function EditRaceForm({ raceId }) {
    const navigate = useNavigate();
    const { data: race, isLoading, error: loadError } = useRace(raceId);
    const { mutateAsync: updateRace, isPending: updating } = useUpdateRace(raceId);
    const { mutateAsync: deleteRace, isPending: deleting } = useDeleteRace();
    const confirm = useConfirm();

    const [form, setForm]           = useState(null);
    const [seededFor, setSeededFor] = useState(null);
    const [localErrors, setLocalErrors]   = useState({});
    const [serverFields, setServerFields] = useState({});

    if (race && seededFor !== race.id) {
        setSeededFor(race.id);
        setForm(buildForm(race));
    }

    const setField = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (localErrors[field] || serverFields[field]) {
            setLocalErrors(prev => ({ ...prev, [field]: undefined }));
            setServerFields(prev => ({ ...prev, [field]: undefined }));
        }
    };
    const fieldError = (field) => localErrors[field] || serverFields[field];

    if (isLoading || (!form && !loadError)) return <div className="cat-form__state">Cargando raza…</div>;
    if (loadError || !race) return <div className="cat-form__state">Raza no encontrada.</div>;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateRace(form);
        if (Object.keys(errors).length) { setLocalErrors(errors); return; }
        setLocalErrors({});
        setServerFields({});

        try {
            await updateRace({ name: cleanName(form.name), code: orNull(form.code), status: form.status });
            toast.success('Raza actualizada');
            navigate(`/admin/races/${raceId}`);
        } catch (err) {
            if (err?.status === 409) toast.error('Ya existe una raza con ese nombre o código');
            else if (err?.fields) { setServerFields(err.fields); toast.error('Revisá los campos marcados'); }
            else toast.error(err?.message || 'No se pudo actualizar la raza');
        }
    };

    const handleDelete = async () => {
        const ok = await confirm({
            title: 'Eliminar raza',
            message: `¿Eliminar la raza "${race.name}"?`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await deleteRace(raceId);
            toast.success('Raza eliminada');
            navigate('/admin/races');
        } catch (err) {
            if (err?.status === 409) toast.error('No se puede eliminar: tiene registros asociados');
            else toast.error(err?.message || 'No se pudo eliminar la raza');
        }
    };

    return (
        <form className="cat-form" onSubmit={handleSubmit}>
            <header className="cat-form__header">
                <Link to={`/admin/races/${raceId}`} className="cat-form__back">Volver a la raza</Link>
                <h1 className="cat-form__title">Editar raza</h1>
                <span className="cat-form__subtitle">#{race.id}</span>
            </header>

            <RaceFields form={form} setField={setField} fieldError={fieldError} disabled={updating} showStatus />

            <footer className="cat-form__footer">
                <Link to={`/admin/races/${raceId}`} className="cat-form__cancel">Cancelar</Link>
                <Gate domain="races" action="write">
                    <button type="button" className="cat-form__danger" onClick={handleDelete} disabled={updating || deleting}>
                        {deleting ? 'Eliminando…' : 'Eliminar'}
                    </button>
                </Gate>
                <button type="submit" className="cat-form__submit" disabled={updating || !form.name.trim()}>
                    {updating ? 'Guardando…' : 'Guardar cambios'}
                </button>
            </footer>
        </form>
    );
}
